import { useEffect, useMemo, useState } from "react";
import "./Blog.css";
import BannerSection from "../Pages/Alumni/Partials/Banner";
import { Link } from "react-router-dom";
import api from "../../api";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blogBanner, setBlogBanner] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const resolveImage = (img) => {
    if (!img) return "";
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    if (img.startsWith("/")) return `${process.env.REACT_APP_API_URL || ""}/public${img}`;
    return img;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/blog");
        const data = res.data?.data || [];

        const normalized = (Array.isArray(data) ? data : []).map((p) => ({
          ...p,
          blog_image: p.blog_image ? resolveImage(p.blog_image) : "",
          published_date_raw: p.published_date,
          published_date: p.published_date ? new Date(p.published_date) : null,
        }));

        normalized.sort((a, b) => {
          const ta = a.published_date ? a.published_date.getTime() : 0;
          const tb = b.published_date ? b.published_date.getTime() : 0;
          return tb - ta;
        });

        if (!cancelled) setPosts(normalized);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBlogs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
  const fetchBlogBanner = async () => {
    try {
      const res = await api.get("/api/blog-banner");
      if (res.data?.status) {
        setBlogBanner(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching blog banner:", err);
    }
  };

  fetchBlogBanner();
}, []);

  const featuredBlog = posts[0] || null;
  const otherBlogs = posts.slice(1);

  // Banner logic: Show first index blog image
  const bannerImage = featuredBlog ? featuredBlog.blog_image : "";

  const categories = useMemo(
    () => Array.from(new Set(otherBlogs.map((p) => p.category).filter(Boolean))),
    [otherBlogs]
  );

  const filteredGridPosts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return otherBlogs.filter((p) => {
      if (selectedCategory && p.category !== selectedCategory) return false;
      if (!q) return true;
      const hay = `${p.title || ""} ${p.sub_content || ""} ${p.category || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [otherBlogs, searchQuery, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredGridPosts.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const pagedGridPosts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredGridPosts.slice(start, start + pageSize);
  }, [filteredGridPosts, currentPage]);

  const formatDate = (d) => {
    if (!d) return "";
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    return d.toLocaleDateString("en-GB", opts);
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory((prev) => (prev === cat ? "" : cat));
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <>
        <BannerSection />
        <div className="container py-5 text-center">Loading blogs...</div>
      </>
    );
  }

  if (!posts.length) {
    return (
      <>
        <BannerSection />
        <div className="container py-5 text-center">No blog posts available.</div>
      </>
    );
  }

  return (
    <>
      {/* Banner shows the image from the first blog post */}
      <BannerSection title="Our Blog" customBanner={blogBanner} />

      <section className="latest-blogs-section">
        <div className="container">
          <div className="sec-title text-center">
            <h2>Latest Blogs</h2>
          </div>

          {featuredBlog && (
            <div className="row align-items-center d-flex justify-content-center">
              <div className="col-lg-5">
                <div className="blog-img-wrapper">
                  <Link to={featuredBlog.slug ? `/blog/${featuredBlog.slug}` : "/blog-details"}>
                    <img src={featuredBlog.blog_image} alt={featuredBlog.title} className="img-fluid" />
                  </Link>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="featured-content">
                  <div>
                    <span className="blog-date">{formatDate(featuredBlog.published_date)}</span>
                  </div>
                  <h3>{featuredBlog.title}</h3>
                  <p>{featuredBlog.sub_content}</p>
                  <div>
                    <Link to={featuredBlog.slug ? `/blog/${featuredBlog.slug}` : "/blog-details"} className="blog-btn">
                      Read More
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="side-blogs">
            {otherBlogs.map((post) => (
              <div key={post.id} className="side-blog-card">
                <div className="side-blog-image-wrapper">
                  <Link to={post.slug ? `/blog/${post.slug}` : "/blog-details"}>
                    <img src={post.blog_image} alt={post.title} />
                  </Link>
                </div>

                <div className="side-blog-content">
                  <h4>{post.title}</h4>
                  <div className="side-blog-header">
                    <span className="blog-date">{formatDate(post.published_date)}</span>
                    <Link to={post.slug ? `/blog/${post.slug}` : "/blog-details"} className="blog-btn small">
                      Read More
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="search-section">
            <div className="sec-title">
              <h2>Search</h2>
            </div>
            <form onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search here"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
              <button type="submit">
                <i className="fa fa-search"></i>
              </button>
            </form>
          </div>

          <div className="categories-section">
            <div className="sec-title">
              <h2>Categories</h2>
            </div>
            <div className="category-pills">
              <span
                className={`category-pill ${!selectedCategory ? "active" : ""}`}
                onClick={() => handleCategoryClick("")}
                style={{ cursor: "pointer" }}
              >
                All
              </span>

              {categories.map((cat, index) => (
                <span
                  key={index}
                  className={`category-pill ${selectedCategory === cat ? "active" : ""}`}
                  onClick={() => handleCategoryClick(cat)}
                  style={{ cursor: "pointer" }}
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>

          <div className="blog-grid">
            {pagedGridPosts.map((post) => (
              <div key={post.id} className="grid-blog-card">
                <div className="grid-blog-image-wrapper">
                  <Link to={post.slug ? `/blog/${post.slug}` : "/blog-details"}>
                    <img src={post.blog_image} alt={post.title} />
                  </Link>
                </div>
                <div className="grid-blog-content">
                  <span className="blog-date">{formatDate(post.published_date)}</span>
                  <h4>{post.title}</h4>
                  <p>{post.sub_content}</p>
                  <Link to={post.slug ? `/blog/${post.slug}` : "/blog-details"} className="blog-btn small">
                    Read More
                  </Link>
                </div>
              </div>
            ))}

            {!pagedGridPosts.length && !loading && (
              <div className="col-12 text-center py-4">No posts found in the grid for your search / filter.</div>
            )}
          </div>

          <div className="pagination-section">
            <div className="pagination">
              <button
                className="pagination-btn prev-next"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <i className="fa fa-chevron-left"></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  className={`pagination-btn ${currentPage === pg ? "active" : ""}`}
                  onClick={() => setCurrentPage(pg)}
                >
                  {pg}
                </button>
              ))}

              <button
                className="pagination-btn prev-next"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="admissions-banner mt-3 mt-md-0">
        <div className="container">
          <div className="admissions-content">
            <h2>ADMISSIONS</h2>
            <Link to="https://admission.care.edu.in/" className="apply-now-btn">
              APPLY NOW
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Blog;